import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import AuthHeader from "@/components/AuthHeader";
import { PrimaryButton, SecondaryButton } from "@/components/brand";
import GoogleButton from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { REGISTER } from "@/constants/testIds";
import { REGIONS, PLANS } from "@/constants/routa";
import api, { formatApiError } from "@/lib/api";
import Footer from "@/components/Footer";

const STEPS = ["Account", "Business", "Plan"];

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const initialPlan = PLANS.some((p) => p.id === params.get("plan")) ? params.get("plan") : "growth";
  const gt = params.get("gt");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    company_name: "", name: "", email: "", phone: "", password: "",
    business_address: "", region: REGIONS[0], plan: initialPlan,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(!gt);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Prefill from Google signup token (single-use, 15 min).
  useEffect(() => {
    if (!gt) return;
    (async () => {
      try {
        const { data } = await api.get(`/auth/google/session/${encodeURIComponent(gt)}`);
        setForm((f) => ({ ...f, email: data.email, name: data.name || f.name }));
        setGoogleReady(true);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Google sign-in expired. Please try again.");
      }
    })();
  }, [gt]);

  const next = () => { setError(""); setStep((s) => Math.min(s + 1, 2)); };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const step0Valid = form.name && form.email && (gt || form.password.length >= 6);
  const step1Valid = form.company_name && form.business_address && form.region;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = { ...form, role: "merchant" };
    if (gt) {
      delete payload.password;
      payload.google_signup_token = gt;
    }
    const res = await register(payload);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    navigate("/merchant", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AuthHeader tag="MERCHANT SIGN-UP" />
      <div className="flex-1 flex items-center">
        <div className="mx-auto w-full max-w-lg px-6 py-16">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-4">[MERCHANT]</div>
          <h1 className="headline text-5xl text-[#0a0a0a]">Create merchant account.</h1>
          <p className="mt-5 text-base text-gray-500">Start dispatching AI-optimized deliveries across Canada.</p>

          {/* Step indicator */}
          <div className="mt-8 flex items-center gap-2" data-testid="signup-steps">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <span className={`grid h-6 w-6 place-items-center rounded-full font-mono text-[11px] ${i < step ? "bg-[#0a0a0a] text-white" : i === step ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={`font-mono text-[11px] tracking-[0.12em] uppercase ${i === step ? "text-[#0a0a0a]" : "text-gray-400"}`}>{label}</span>
                {i < STEPS.length - 1 && <span className="h-px w-6 bg-gray-200" />}
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-6">
            {error && <div data-testid="register-error" className="border border-orange-500 bg-orange-50 text-orange-700 text-sm px-4 py-3">{error}</div>}

            {step === 0 && (
              <div className="space-y-6" data-testid="signup-step-account">
                {gt && (
                  <div className="border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600" data-testid="google-prefill-banner">
                    Signing up with Google as <span className="text-[#0a0a0a] font-semibold">{form.email}</span>. No password needed.
                  </div>
                )}
                <div>
                  <label className="routa-label">Contact name</label>
                  <input data-testid={REGISTER.nameInput} required value={form.name} onChange={set("name")} className="routa-input" />
                </div>
                <div>
                  <label className="routa-label">Email</label>
                  <input data-testid={REGISTER.emailInput} type="email" required disabled={!!gt} value={form.email} onChange={set("email")} className="routa-input disabled:bg-gray-50 disabled:text-gray-500" />
                </div>
                <div>
                  <label className="routa-label">Phone</label>
                  <input data-testid="register-phone-input" value={form.phone} onChange={set("phone")} className="routa-input" />
                </div>
                {!gt && (
                  <div>
                    <label className="routa-label">Password</label>
                    <input data-testid={REGISTER.passwordInput} type="password" required minLength={6} value={form.password} onChange={set("password")} className="routa-input" />
                  </div>
                )}
                <PrimaryButton type="button" data-testid="signup-next-account" onClick={next} disabled={!step0Valid || !googleReady} className="w-full">Continue</PrimaryButton>
                {!gt && (
                  <>
                    <div className="flex items-center gap-4">
                      <span className="h-px flex-1 bg-gray-200" />
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">or</span>
                      <span className="h-px flex-1 bg-gray-200" />
                    </div>
                    <GoogleButton intent="merchant" label="Sign up with Google" />
                  </>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6" data-testid="signup-step-business">
                <div>
                  <label className="routa-label">Company name</label>
                  <input data-testid="register-company-input" required value={form.company_name} onChange={set("company_name")} className="routa-input" />
                </div>
                <div>
                  <label className="routa-label">Business address</label>
                  <input data-testid="register-address-input" required value={form.business_address} onChange={set("business_address")} className="routa-input" />
                </div>
                <div>
                  <label className="routa-label">Primary region</label>
                  <select data-testid="register-region-select" value={form.region} onChange={set("region")} className="routa-input">
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="flex gap-3">
                  <SecondaryButton type="button" data-testid="signup-back-business" onClick={back} className="flex-1">Back</SecondaryButton>
                  <PrimaryButton type="button" data-testid="signup-next-business" onClick={next} disabled={!step1Valid} className="flex-1">Continue</PrimaryButton>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="signup-step-plan">
                <div className="space-y-3">
                  {PLANS.map((p) => (
                    <label
                      key={p.id}
                      data-testid={`signup-plan-${p.id}`}
                      className={`flex items-center justify-between gap-4 border px-4 py-3.5 cursor-pointer transition-colors ${form.plan === p.id ? "border-orange-500 bg-orange-50" : "border-gray-300 hover:border-[#0a0a0a]"}`}
                    >
                      <div className="flex items-center gap-3">
                        <input type="radio" name="plan" value={p.id} checked={form.plan === p.id} onChange={set("plan")} className="accent-orange-500" />
                        <div>
                          <div className="text-sm font-semibold text-[#0a0a0a]">{p.name} {p.popular && <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-orange-600 ml-1">Popular</span>}</div>
                          <div className="text-xs text-gray-500">{p.tagline}</div>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-[#0a0a0a] whitespace-nowrap">${p.price}<span className="text-xs font-normal text-gray-400">/mo</span></div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500">Your account will be submitted for approval. You won't be charged until your account is approved and activated.</p>
                <div className="flex gap-3">
                  <SecondaryButton type="button" data-testid="signup-back-plan" onClick={back} className="flex-1">Back</SecondaryButton>
                  <PrimaryButton orange arrow type="submit" data-testid={REGISTER.submitButton} disabled={busy} className="flex-1">
                    {busy ? "Submitting…" : "Submit for approval"}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </form>

          <div className="mt-8 text-sm text-gray-500">
            Already have an account? <Link to="/login?role=merchant" data-testid={REGISTER.loginLink} className="underline text-[#0a0a0a]">Sign in</Link>.
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
